import {
  type LiveContextTransform,
  type LiveValuesTransform,
} from '@framework/validation/live/LiveTypes.ts';
import { uniq } from 'lodash';
import { type IEngineSection } from '../schema/sections/IEngineSection.ts';
import { collectPathsBySemanticTagId } from './form-builder/semanticTags/collectPathsBySemanticTagId.ts';
import { createSemanticFieldsContextTransform } from './liveContext/createSemanticFieldsContextTransform.ts';
import { createSemanticTagSyncValuesTransform } from './liveContext/createSemanticTagSyncValuesTransform.ts';

/**
 * Punto d'ingresso build-time del live context engine.
 *
 * Chiamato da `FormValidatorBuilder.build()` per agganciare allo schema:
 * - `contextTransform`: popola `context.semanticFields` a runtime;
 * - `valuesTransform`: allinea i valori tra widget con stesso tag univoco;
 * - `liveFieldPaths`: path RHF osservati dalla UI per re-render live.
 */
export function setupEngineLiveContext(sections: IEngineSection[]): {
  contextTransform: LiveContextTransform;
  valuesTransform: LiveValuesTransform;
  liveFieldPaths: readonly string[];
} {
  const pathsBySemanticTagId = collectPathsBySemanticTagId(sections);

  const selectedPathBySemanticTagId =
    selectSemanticFieldSourcePathByTagId(pathsBySemanticTagId);

  return {
    contextTransform: createSemanticFieldsContextTransform(
      selectedPathBySemanticTagId,
    ),
    valuesTransform: createSemanticTagSyncValuesTransform(pathsBySemanticTagId),
    liveFieldPaths: getLiveFieldPaths(pathsBySemanticTagId),
  };
}

function getLiveFieldPaths(
  pathsBySemanticTagId: Map<string, string[]>,
): string[] {
  return uniq(Array.from(pathsBySemanticTagId.values()).flat());
}

/**
 * Per `$semanticFields` serve un solo path per tag.
 *
 * Conserva il comportamento storico: se più widget esportano stesso tag,
 * vince l'ultimo path incontrato nella scansione.
 */
function selectSemanticFieldSourcePathByTagId(
  pathsBySemanticTagId: Map<string, string[]>,
): Map<string, string> {
  const selectedPathBySemanticTagId = new Map<string, string>();

  for (const [semanticTagId, rhfPaths] of pathsBySemanticTagId) {
    const selectedPath = rhfPaths[rhfPaths.length - 1];
    if (selectedPath) {
      selectedPathBySemanticTagId.set(semanticTagId, selectedPath);
    }
  }

  return selectedPathBySemanticTagId;
}
